<template>
  <div class="dropdown" :class="[detailsClass, { active: isOpen }]">
    <component
      :is="hasHeader ? 'div' : 'button'"
      class="dropdown__summary"
      :class="[summaryClass]"
      :aria-expanded="!hasHeader ? isOpen : null"
      :aria-label="!hasHeader ? dropdownAriaLabel : null"
      :aria-controls="contentUniqueId"
      @click="() => !hasHeader && toggleDropdown()"
    >
      <template v-if="!hasHeader">
        <span class="dropdown__title">{{ props.title }}</span>
        <span class="dropdown__subtitle">{{ props.subtitle }}</span>
        <span class="dropdown__icon s s-arrow-down-fat" aria-hidden="true"></span>
      </template>
      <template v-else>
        <slot name="header"></slot>
        <button
          class="dropdown__icon s s-arrow-down-fat"
          :aria-controls="contentUniqueId"
          :aria-expanded="String(isOpen)"
          @click="toggleDropdown()"
        ></button>
      </template>
    </component>
    <div :id="contentUniqueId" ref="content" class="dropdown__content" :inert="!isOpen" :aria-hidden="!isOpen">
      <fieldset>
        <legend class="a11y-visually-hidden">{{ props.title }}</legend>

        <div :class="contentClass">
          <slot name="content"></slot>
        </div>
      </fieldset>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, useSlots } from 'vue'

import { loadStyles } from '../../mixins/LoadStyles/LoadStyles'

/**
 * Styles
 */
const COMPONENT_NAME = 'Dropdown'
loadStyles({ name: COMPONENT_NAME })

/**
 * Inject
 */
const i18n = inject('i18n')

const isOpen = ref(true)
const content = ref(null)
const contentUniqueId = `dropdown-content-${Math.random().toString(36).substr(3, 6)}`

/**
 * Props
 */
const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  subtitle: {
    type: String,
    default: '',
  },
  hideSubtitle: {
    type: Boolean,
    default: false,
  },
  detailsClass: {
    type: String,
    default: '',
  },
  summaryClass: {
    type: String,
    default: '',
  },
  contentClass: {
    type: String,
    default: '',
  },
})

/**
 * Slots
 */
const slots = useSlots()

const hasHeader = computed(() => {
  return slots.header
})
const dropdownAriaLabel = computed(() => {
  const action = isOpen.value ? i18n.value.commons.collapseSection : i18n.value.commons.expandSection
  return `${action} ${props.title}`
})

/**
 * Methods
 */

const resizeContent = () => {
  if (isOpen.value) {
    const height = content.value.scrollHeight
    content.value.style.maxHeight = `${height}px`
  } else {
    content.value.style.maxHeight = '0px'
    content.value.style.padding = '0px'
  }
}

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  resizeContent()
}

/**
 * Lifecycle
 */
onMounted(() => {
  content.value.addEventListener('transitionend', () => {
    if (isOpen.value) {
      resizeContent()
    }
  })
})
</script>
